import { config } from '@/config';
import { DEFAULT_AVATAR } from '@/helpers/assetUrl';
import { openExternal } from '@/helpers/openExternal';
import { Navigation } from '@/components/shared/navigation';
import './Header.scss';
import { Badge, Flex, Grid, Image } from 'antd';
import { Modal } from '@/components/pages/my-profile/assets';
import { Logo } from '@/components/logo';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/types/routes';
import { removeUser, selectUser } from '@/slice/user';
import { useAppSelector } from '@/hooks/useAppSelector';
import { Fragment } from 'react/jsx-runtime';
import { ButtonWithIcon } from '@/components/ui/ButtonWithIcon';
import { useEffect, useRef, useState } from 'react';
import { SvgIcon } from '@/components/icon';
import { AvatarPlaceholder } from '@/components/shared/avatar-placeholder';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getNotifications, markNotificationRead } from '@/api/notificattions';
import { notificationsQueryKey } from '@/helpers/auth';
import { NotificationsComponent } from '@/components/notifications/NotificationsComponent';
import { Notification, UserRole } from '@/types';
import { RequestHelp } from '@/components/modals/RequestHelp';
import { CustomButton } from '@/components/ui';
import { ProfileDropdown } from '@/components/my-profile';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { logout } from '@/api/auth';
import { endSession } from '@/client/axiosInstance';
import { disconnectSocket } from '@/client/socket';
import { ReactComponent as TrustedProBadge } from '@/assets/icons/trusted_pro_badge.svg';

const navigationPublic = [
  { path: ROUTES.SIGN_IN, label: 'My Profile', icon: 'user' },
  { path: ROUTES.SIGN_IN, label: 'Messages', icon: 'chat' },
  { path: ROUTES.SIGN_IN, label: 'My Meetings', icon: 'calendar' },
  { path: ROUTES.SIGN_IN, label: 'Payments', icon: 'creditCard' },
];

// True when the visitor is anonymous AND viewing a consultant's public
// profile page (route `/consultants/:id`). Used to hide the
// auth-prompting nav items + the "Unlock Premium" banner so the public
// profile reads like a standalone marketing page rather than a logged-
// out app shell. See TCX-577.
const isPublicConsultantProfileView = (pathname: string, isAuthed: boolean) =>
  !isAuthed && /^\/consultants\/[^/]+$/.test(pathname);

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsModalOpen, setNotificationsModelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [notificationsDisplayed, setNotificationsDisplayed] = useState<
    Notification[]
  >([]);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { lg } = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('accessToken');
  const [profileVisible, setProfileVisible] = useState(false);

  // Trusted Pro is a consultant-only designation (`isProMember`). When set, the
  // header avatar gets a blue ring + gold shield badge and the profile dropdown
  // shows an informational card (TCX-617).
  const isTrustedPro =
    user.role === UserRole.CONSULTANT && !!user.isProMember;

  const handleEditProfile = () => {
    navigate(ROUTES.MY_PROFILE);
    setProfileVisible(false);
  };

  const handleSettings = () => {
    navigate(ROUTES.MY_PROFILE_CHANGE_PASSWORD);
    setProfileVisible(false);
  };

  const handleSupport = () => {
    setModalOpen(!modalOpen);
    setProfileVisible(false);
  };

  const handleResourceCenter = () => {
    // Consultants have a dedicated resource center URL (TCX-530). Other
    // roles fall back to the default. Both values come from runtime config
    // via window.__APP_CONFIG__ / /config.js.
    const url =
      user?.role === UserRole.CONSULTANT
        ? config.VITE_CONSULTANT_RESOURCE_CENTER_URL ||
          config.VITE_RESOURCE_CENTER_URL
        : config.VITE_RESOURCE_CENTER_URL;

    if (!url) {
      console.error('Resource Center URL is not configured');
      return;
    }

    openExternal(url);
    setProfileVisible(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setLogoutModalOpen(false);
      dispatch(removeUser());
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setLogoutModalOpen(false);
      dispatch(removeUser());
      window.location.href = '/';
    }
  };

  const handlePremiumDashboard = () => {
    const url = user?.premiumDashboardUrl;
    if (!url) return;
    // Admin-entered URL — open in a new tab with opener hardening.
    window.open(url, '_blank', 'noopener,noreferrer');
    setProfileVisible(false);
  };

  const menuOptions = [
    {
      icon: <SvgIcon type="settings" />,
      label: 'Settings',
      onClick: handleSettings,
    },
    // TCX-737: franchisors with a saved TCX Premium Dashboard URL get a menu
    // entry (under Settings, above Resource Center) that opens it in a new tab.
    ...(user?.role === UserRole.FRANCHISEE && user?.premiumDashboardUrl
      ? [
          {
            icon: <SvgIcon type="premiumDashboard" />,
            label: 'TCX Premium Dashboard',
            onClick: handlePremiumDashboard,
          },
        ]
      : []),
    {
      icon: <SvgIcon type="book" />,
      label: 'Resource Center',
      onClick: handleResourceCenter,
    },
    {
      icon: <SvgIcon type="help" />,
      label: 'Support',
      onClick: handleSupport,
    },
    {
      icon: <SvgIcon type="logoutDark" />,
      label: 'Log Out',
      onClick: handleLogout,
    },
  ];

  const { data: notifications, isFetching } = useQuery({
    queryKey: notificationsQueryKey(page),
    queryFn: () => getNotifications({ page }),
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!notifications) return;

    if (notifications.length === 0) {
      setHasMore(false);
      return;
    }

    if (page === 1) {
      setNotificationsDisplayed(notifications);
    } else {
      setNotificationsDisplayed((prev) => [...prev, ...notifications]);
    }
  }, [notifications, page, notificationsModalOpen]);

  const { mutateAsync: markNotificationsRead } = useMutation({
    mutationFn: () => markNotificationRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleLoadMore = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setProfileVisible(false);
  };

  const toggleNotificationsModal = () => {
    if (!notificationsModalOpen) {
      setPage(1);
      setHasMore(true);
      setNotificationsDisplayed([]);
    }
    setNotificationsModelOpen(!notificationsModalOpen);
    setProfileVisible(false);
  };

  return (
    <Fragment>
      {lg ? (
        <Flex className={'header'} gap={24}>
          <div style={{ marginRight: '40px' }}>
            <Logo />
          </div>

          {isAuthenticated ? (
            <Fragment>
              <Navigation />
              <div
                style={{
                  position: 'relative',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  gap: 12,
                }}
              >
                <ButtonWithIcon
                  style={{
                    background: 'transparent',
                    height: '32px',
                    width: '32px',
                  }}
                  icon={
                    <>
                      <SvgIcon type={'bell'} />
                      {(notifications && notifications[0]?.isRead) ===
                        false && (
                        <Badge
                          style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '6px',
                          }}
                          color="#2A4BC2"
                          dot
                        />
                      )}
                    </>
                  }
                  onClick={toggleNotificationsModal}
                />

                <NotificationsComponent
                  visible={notificationsModalOpen}
                  onMarkAsRead={markNotificationsRead}
                  notifications={notificationsDisplayed}
                  loading={isFetching}
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                  onClose={() => setNotificationsModelOpen(false)}
                />
              </div>

              <div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  cursor: 'pointer',
                }}
              >
                <div
                  className={`profile-button${
                    isTrustedPro ? ' profile-button--trusted' : ''
                  }`}
                >
                  {user.profilePhoto?.length ? (
                    <Image
                      onClick={() => setProfileVisible(!profileVisible)}
                      wrapperClassName={
                        user?.role === UserRole.FRANCHISEE
                          ? 'profile-image profile-image--logo'
                          : 'profile-image'
                      }
                      preview={false}
                      src={user?.profilePhoto}
                      height={40}
                      width={40}
                      fallback={DEFAULT_AVATAR}
                    />
                  ) : (
                    <div
                      onClick={() => setProfileVisible(!profileVisible)}
                      style={{ width: '40px', height: '40px' }}
                    >
                      <AvatarPlaceholder
                        firstName={user?.firstName || ''}
                        lastName={user?.lastName || ''}
                      />
                    </div>
                  )}
                </div>
                {isTrustedPro && (
                  <span className="profile-trusted-badge">
                    <TrustedProBadge />
                  </span>
                )}
                <ProfileDropdown
                  visible={profileVisible}
                  isTrustedPro={isTrustedPro}
                  onClose={() => setProfileVisible(false)}
                  businessName={
                    user.role === UserRole.CONSULTANT
                      ? `${user?.firstName} ${user?.lastName}`
                      : user?.company || '-'
                  }
                  userName={
                    user.role === UserRole.CONSULTANT
                      ? // TCX-507: groups may be {id,name} refs or name strings
                        user?.groups
                          ?.map((g: any) => (typeof g === 'string' ? g : g?.name))
                          .join(', ') || '-'
                      : user?.categories?.join(', ') || '-'
                  }
                  userId={
                    user.role === UserRole.CONSULTANT
                      ? user?._id
                      : user['franchise']
                  }
                  onEditProfile={handleEditProfile}
                  menuOptions={menuOptions}
                />
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div className={'navigation'}>
                <nav className={'navigation-list'}>
                  {!isPublicConsultantProfileView(
                    location.pathname,
                    isAuthenticated,
                  ) &&
                    navigationPublic.map(({ path, label }) => (
                      <NavLink
                        to={path}
                        state={{ from: location }}
                        key={path + '-' + label}
                        className={({ isActive }) =>
                          isActive ? 'active link' : 'link'
                        }
                        style={{ position: 'relative' }}
                      >
                        {label}
                      </NavLink>
                    ))}
                </nav>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                <CustomButton
                  type="primary"
                  onClick={() =>
                    navigate(ROUTES.SIGN_IN, {
                      state: {
                        from: location,
                      },
                    })
                  }
                  text="LOG IN"
                />
                <CustomButton
                  type="default"
                  onClick={() =>
                    navigate(ROUTES.SIGN_UP, {
                      state: {
                        from: location,
                      },
                    })
                  }
                  text="SIGN UP"
                />
              </div>
            </Fragment>
          )}
        </Flex>
      ) : (
        <Fragment>
          <MobileHeader
            actions={
              isAuthenticated ? (
                <Fragment>
                  <div
                    style={{
                      position: 'relative',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ButtonWithIcon
                      icon={
                        <>
                          <SvgIcon type={'bell'} />
                          {(notifications && notifications[0]?.isRead) ===
                            false && (
                            <Badge
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '13px',
                              }}
                              color="#2A4BC2"
                              size="default"
                              dot
                            />
                          )}
                        </>
                      }
                      onClick={toggleNotificationsModal}
                    />

                    <NotificationsComponent
                      visible={notificationsModalOpen}
                      onMarkAsRead={markNotificationsRead}
                      notifications={notificationsDisplayed}
                      loading={isFetching}
                      hasMore={hasMore}
                      onLoadMore={handleLoadMore}
                      onClose={() => setNotificationsModelOpen(false)}
                    />
                  </div>

                  <ButtonWithIcon
                    icon={<SvgIcon type={'burgerMenu'} />}
                    onClick={toggleMenu}
                  />
                </Fragment>
              ) : (
                <Fragment>
                  <CustomButton
                    type="primary"
                    onClick={() => navigate(ROUTES.SIGN_IN)}
                    text="LOG IN"
                  />
                  <CustomButton
                    type="default"
                    onClick={() => navigate(ROUTES.SIGN_UP)}
                    text="SIGN UP"
                  />
                </Fragment>
              )
            }
          />

          {isAuthenticated && (
            <Navigation
              open={menuOpen}
              onClose={toggleMenu}
              user={user}
              onLogoutClick={handleLogout}
              onSupportClick={handleSupport}
              isAuthenticated={isAuthenticated}
            />
          )}
        </Fragment>
      )}
      {isAuthenticated && (
        <RequestHelp open={modalOpen} onCancel={() => setModalOpen(false)} />
      )}
      <Modal
        open={logoutModalOpen}
        title={'Log out now?'}
        description={
          'Are you sure you want to log out of your account? You’ll need to re-enter your credentials to access the app again.'
        }
        cancelText={'CANCEL'}
        okText={'LOG OUT'}
        onCancel={() => setLogoutModalOpen(false)}
        onOk={() => {
          // TCX-838: end the session (bump generation + drop the Authorization
          // default) and drop the authenticated socket before clearing storage,
          // so this logout tears down the same client state as api logout().
          endSession();
          disconnectSocket();
          localStorage.clear();
          sessionStorage.clear();

          setModalOpen(false);

          dispatch(removeUser());

          window.location.href = '/';
        }}
      />
    </Fragment>
  );
};

export { Header };
